/**
 * Classification: what IS this file?
 *
 * The parsers answer "is this SRT?" through their own `sniff`. This module answers the
 * questions that come BEFORE that, and each of them routes to a different place in the product:
 *
 *   • **A video container** → CORE's ffmpeg host extracts the embedded subtitle track, and the
 *     codec it reports maps to a `ReadFormat` here. CORE owns the host; the format side is
 *     ours, and `src/worker/adapter/container.ts` says so in its own header.
 *   • **A BITMAP subtitle track** (PGS, VobSub, DVD, XSUB) → the "we can't read this file type"
 *     path, B5's fourth message. These are IMAGES of text; they need OCR, which is P2. A
 *     bitmap track must never reach a parser, because a parser handed a bitmap produces zero
 *     cues and no explanation.
 *   • **An audio file** → the transcribe path, B6, and this is the one place in Path B where
 *     the free-and-unlimited promise stops applying. It must be SAID before anything runs.
 *   • **A bitmap subtitle FILE** (`.sup`, `.idx`/`.sub` VobSub pair) → the same B5 message,
 *     reached without a container at all.
 *
 * Everything here is PURE and works on bytes plus a filename. Sniffing by extension alone is
 * not enough — a `.sub` is MicroDVD text or a VobSub bitstream depending on its contents, and
 * getting that wrong is the difference between a conversion and a confusing error.
 */

import type { ReadFormat } from '../../contracts/cue-model.ts';
import { hasXmlDeclaration, readStartTag, rootStart } from './common/xml.ts';

/*───────────────────────────────────────────────────────────────────────────────────────
  Containers
───────────────────────────────────────────────────────────────────────────────────────*/

export type ContainerKind = 'matroska' | 'mp4' | 'avi' | 'mpeg-ts' | 'flv' | 'ogg';

/**
 * Which containers THIS BUILD can take a subtitle track out of — founder ruling, CTO S25 §1.
 *
 * `@ffmpeg/core` is not shipped at all, so the GPL-2.0-or-later question stops existing and the
 * reader is a pure-JS demuxer CORE builds in the host: EBML clusters for Matroska, the
 * `stsd`/`stts`/`stco` tables for MP4. Everything else is refused.
 *
 * **This list lives here because it is format knowledge**, which is the line
 * `src/worker/adapter/container.ts` already draws in its own header — *"container sniffing,
 * subtitle-codec → ReadFormat mapping, and bitmap classification … belong to CONVERT"*. Putting it in
 * the walker instead would make it a SECOND list that has to agree with `ContainerKind`, and this
 * project has produced that defect twice in a week: a duplicated language map in `resegment/op.ts`,
 * and a CJK bucket coarser than the consumer's. The host asks; it does not know.
 */
const DEMUXABLE: ReadonlySet<ContainerKind> = new Set<ContainerKind>(['matroska', 'mp4']);

/**
 * Why a container cannot be read, or `null` when it can. The technical reason, in the register the
 * rest of this file uses — UI adapts the wording, as it does for `unreadable-binary`.
 *
 * The AVI and TS answers are the ones that make the founder's ruling cheap rather than a gamble, and
 * both are findings from CONVERT S6 §4: **AVI has no standard soft-subtitle track at all** — its
 * subtitles are external `.srt`/`.sub` files sitting beside the video, which this product already
 * reads as ordinary uploads — and **MPEG-TS carries DVB bitmap or teletext**, which `sniffBitmapFile`
 * already routes to the "we can't read this file type" path. So the refusal is not a gap in coverage
 * for either: it is telling the user something true about their file.
 */
export function containerRefusal(container: ContainerKind): string | null {
  if (DEMUXABLE.has(container)) return null;
  switch (container) {
    case 'avi':
      return 'AVI has no standard subtitle track — AVI subtitles are separate .srt or .sub files saved next to the video, and those we can read. Look for one beside this file.';
    case 'mpeg-ts':
      return 'a transport stream carries its subtitles as DVB bitmaps or teletext, which are pictures of text rather than text. Nothing in this file can be converted to a subtitle file.';
    default:
      return `${container} files are not read for subtitle tracks. MKV, WebM, MP4 and MOV are.`;
  }
}

const CONTAINER_EXTENSIONS: Readonly<Record<string, ContainerKind>> = {
  mkv: 'matroska',
  mka: 'matroska',
  webm: 'matroska',
  mp4: 'mp4',
  m4v: 'mp4',
  mov: 'mp4',
  m4a: 'mp4',
  '3gp': 'mp4',
  avi: 'avi',
  ts: 'mpeg-ts',
  m2ts: 'mpeg-ts',
  mts: 'mpeg-ts',
  flv: 'flv',
  ogv: 'ogg',
};

/**
 * MAGIC BYTES, which is the only reliable answer. An extension is a hint a user can rename
 * away, and a container misidentified as text produces a parse error full of binary garbage.
 *
 *   Matroska/WebM   EBML header `1A 45 DF A3`
 *   MP4/MOV/3GP     `ftyp` at offset 4 (an ISO base-media box)
 *   AVI             `RIFF....AVI `
 *   MPEG-TS         sync byte 0x47 every 188 bytes
 *   FLV             `FLV`
 *   Ogg             `OggS`
 */
export function sniffContainer(bytes: Uint8Array, filename?: string | null): ContainerKind | null {
  if (bytes.length >= 4) {
    if (bytes[0] === 0x1a && bytes[1] === 0x45 && bytes[2] === 0xdf && bytes[3] === 0xa3) {
      return 'matroska';
    }
    if (ascii(bytes, 0, 4) === 'RIFF' && ascii(bytes, 8, 4) === 'AVI ') return 'avi';
    if (ascii(bytes, 0, 3) === 'FLV') return 'flv';
    if (ascii(bytes, 0, 4) === 'OggS') return 'ogg';
  }
  if (bytes.length >= 12 && ascii(bytes, 4, 4) === 'ftyp') return 'mp4';
  // MPEG-TS has no signature, only a periodic sync byte. Three consecutive packets is enough
  // to be sure and cheap enough to check.
  if (bytes.length >= 188 * 3 + 1) {
    if (bytes[0] === 0x47 && bytes[188] === 0x47 && bytes[376] === 0x47 && bytes[564] === 0x47) {
      return 'mpeg-ts';
    }
  }
  // Only then fall back to the extension, and only when the bytes are not text: a `.mkv` whose
  // content is an SRT is an SRT, and a user who renamed a file should still get their subtitles.
  const ext = extensionOf(filename);
  const byExtension = ext === null ? undefined : CONTAINER_EXTENSIONS[ext];
  if (byExtension !== undefined && looksBinary(bytes)) return byExtension;
  return null;
}

/*───────────────────────────────────────────────────────────────────────────────────────
  Container subtitle codecs → ReadFormat
───────────────────────────────────────────────────────────────────────────────────────*/

/**
 * The codec names ffmpeg reports for a subtitle stream, mapped to the format whose parser can
 * read the extracted bytes.
 *
 * `mov_text` (3GPP timed text, the MP4 native subtitle codec) has no text serialisation of its
 * own that a parser reads; ffmpeg's `-c:s text` converts it to SRT on the way out, which is
 * why it maps to `srt` rather than to a format of its own.
 *
 * `eia_608` / `eia608` is closed-caption data carried in the video stream. ffmpeg can decode
 * it to SRT, so it maps there too — but it is worth naming, because it is the one codec whose
 * presence means the file has captions that are not a subtitle TRACK.
 */
const CODEC_TO_FORMAT: Readonly<Record<string, ReadFormat>> = {
  subrip: 'srt',
  srt: 'srt',
  text: 'srt',
  mov_text: 'srt',
  tx3g: 'srt',
  eia_608: 'srt',
  eia608: 'srt',
  cc_dec: 'srt',
  webvtt: 'vtt',
  vtt: 'vtt',
  ass: 'ass',
  ssa: 'ssa',
  'ass/ssa': 'ass',
  microdvd: 'sub',
  mpl2: 'mpl2',
  sami: 'smi',
  realtext: 'txt',
  subviewer: 'srt',
  subviewer1: 'srt',
  stl: 'srt',
  jacosub: 'srt',
};

/**
 * BITMAP subtitle codecs. These are images of text and no parser can read them — they need
 * OCR, which is P2. They must reach B5's "we can't read this file type", never a parser.
 */
const BITMAP_CODECS: ReadonlySet<string> = new Set([
  'hdmv_pgs_subtitle',
  'pgssub',
  'pgs',
  'dvd_subtitle',
  'dvdsub',
  'vobsub',
  'xsub',
  'dvb_subtitle',
  'dvbsub',
  'dvb_teletext', // teletext pages are rendered, not text we can extract here
  'hdmv_text_subtitle',
]);

export type TrackClass =
  | { readonly kind: 'text'; readonly format: ReadFormat }
  | { readonly kind: 'bitmap'; readonly codec: string; readonly needsOcr: true }
  | { readonly kind: 'unknown'; readonly codec: string };

/** Classifies one container subtitle track by its codec name. */
export function classifyTrackCodec(codec: string): TrackClass {
  const key = codec.trim().toLowerCase();
  if (BITMAP_CODECS.has(key)) return { kind: 'bitmap', codec: key, needsOcr: true };
  const format = CODEC_TO_FORMAT[key];
  if (format !== undefined) return { kind: 'text', format };
  return { kind: 'unknown', codec: key };
}

/** True for a codec that is an image rather than text. The B5 gate. */
export function isBitmapCodec(codec: string): boolean {
  return BITMAP_CODECS.has(codec.trim().toLowerCase());
}

/**
 * The `ReadFormat` for a track, or null when nothing can read it.
 *
 * A null is NOT a fallback to SRT. Guessing SRT for an unknown codec means ffmpeg is asked to
 * convert something it may not be able to and the user gets an empty file instead of a reason.
 */
export function formatForCodec(codec: string): ReadFormat | null {
  const classified = classifyTrackCodec(codec);
  return classified.kind === 'text' ? classified.format : null;
}

/*───────────────────────────────────────────────────────────────────────────────────────
  Bitmap subtitle FILES
───────────────────────────────────────────────────────────────────────────────────────*/

export type BitmapFile = 'pgs-sup' | 'vobsub-idx' | 'vobsub-sub' | 'dvd-ifo';

/**
 * A bitmap subtitle dropped as a FILE rather than found inside a container.
 *
 *   `.sup`   PGS, whose segments each begin with the magic `PG` (0x50 0x47)
 *   `.idx`   VobSub's index — text, but it starts with a recognisable banner
 *   `.sub`   AMBIGUOUS: MicroDVD text, or a VobSub MPEG program stream. The discriminator is
 *            the MPEG pack start code `00 00 01 BA`, which no text file begins with.
 */
export function sniffBitmapFile(bytes: Uint8Array, filename?: string | null): BitmapFile | null {
  const ext = extensionOf(filename);
  if (
    bytes.length >= 2 &&
    bytes[0] === 0x50 &&
    bytes[1] === 0x47 &&
    (ext === 'sup' || ext === null)
  ) {
    return 'pgs-sup';
  }
  if (
    bytes.length >= 4 &&
    bytes[0] === 0x00 &&
    bytes[1] === 0x00 &&
    bytes[2] === 0x01 &&
    bytes[3] === 0xba
  ) {
    return 'vobsub-sub';
  }
  if (ext === 'idx') {
    const head = ascii(bytes, 0, Math.min(256, bytes.length));
    if (/VobSub index file/i.test(head) || /^#\s*VobSub/im.test(head)) return 'vobsub-idx';
  }
  if (ext === 'ifo' && bytes.length >= 12 && ascii(bytes, 0, 12) === 'DVDVIDEO-VTS')
    return 'dvd-ifo';
  return null;
}

/** The B5 label for a bitmap file, interpolated into "This looks like {…}". */
export function bitmapLabel(kind: BitmapFile): string {
  switch (kind) {
    case 'pgs-sup':
      return 'a SUP/PGS bitmap subtitle';
    case 'vobsub-idx':
    case 'vobsub-sub':
      return 'a VobSub bitmap subtitle';
    case 'dvd-ifo':
      return 'a DVD VTS index';
  }
}

/*───────────────────────────────────────────────────────────────────────────────────────
  XML: TTML, or some other XML
───────────────────────────────────────────────────────────────────────────────────────*/

/**
 * The namespaces a TTML document element lives in. TTML 1 and 2 share the first; the other two
 * are the W3C drafts DFXP was published under, and the 2006/10 one is what Netflix and most
 * streaming-service DFXP files still declare. Styling, parameter and metadata attributes hang
 * off each as `#styling` (or `#style`, which the Netflix files use), `#parameter` and `#metadata`.
 */
export const TTML_NAMESPACES: readonly string[] = [
  'http://www.w3.org/ns/ttml',
  'http://www.w3.org/2006/10/ttaf1',
  'http://www.w3.org/2006/04/ttaf1',
];

/** The base of a TTML namespace, or null. Accepts `#styling`-style suffixes and a trailing `/`. */
export function ttmlNamespaceBase(namespace: string | null): string | null {
  if (namespace === null) return null;
  const hash = namespace.indexOf('#');
  const base = (hash < 0 ? namespace : namespace.slice(0, hash)).replace(/\/+$/, '');
  return TTML_NAMESPACES.includes(base) ? base : null;
}

/**
 * Other XML caption and edit-list formats people try, named so the refusal can say what the file
 * is. The copy promises only that these are not supported; naming the format is what makes the
 * refusal useful rather than a shrug. Keyed by the document element's local name, lower-cased.
 */
const OTHER_XML: Readonly<Record<string, string>> = {
  xmeml: 'Final Cut Pro XML',
  fcpxml: 'Final Cut Pro X XML',
  timedtext: 'YouTube timed text XML',
  transcript: 'YouTube transcript XML',
  dcsubtitle: 'DCP subtitle XML (Interop)',
  subtitlereel: 'DCP subtitle XML (SMPTE)',
  smil: 'SMIL',
};

export type XmlClass =
  | { readonly kind: 'ttml'; readonly namespace: string | null }
  /** `label` names a known format (`Final Cut Pro XML`); null for XML we do not recognise. */
  | { readonly kind: 'other-xml'; readonly root: string; readonly label: string | null };

const XML_EXTENSIONS: ReadonlySet<string> = new Set(['xml', 'ttml', 'dfxp']);

/**
 * Whether decoded text is a TTML document, some other XML, or not XML at all (`null`). It is
 * the routing rule the TTML reader's `sniff` and its refusal both read, so the two cannot
 * disagree about which file is which.
 *
 * TTML is a document element named `tt` in one of `TTML_NAMESPACES`, after the BOM, whitespace,
 * XML declaration, comments and DOCTYPE. A `tt` element with no namespace, or an unknown one, is
 * accepted too when the file says it is XML (a `.ttml`, `.dfxp` or `.xml` name, or an XML
 * declaration): no other XML caption format has a `tt` document element, and /ttml-to-srt tells
 * people that a file which "starts with a `<tt` tag" will work. Without either signal it is left
 * alone, since a bare `<tt>` pasted as text is as likely to be HTML's teletype element.
 *
 * "Other XML" is claimed only when the file says it is XML, by its extension or by an XML
 * declaration, so an HTML page or a bare `<p>hello</p>` pasted as text stays plain text. SAMI is
 * never claimed: it is SMI's, and an `<?xml?>` line in front of `<SAMI>` does not change that.
 */
export function classifyXml(text: string, filename?: string | null): XmlClass | null {
  const at = rootStart(text.length > 65_536 ? text.slice(0, 65_536) : text);
  if (at < 0) return null;
  const tag = readStartTag(text, at);
  if (tag === null) return null;
  const colon = tag.name.indexOf(':');
  const prefix = colon < 0 ? '' : tag.name.slice(0, colon);
  const local = colon < 0 ? tag.name : tag.name.slice(colon + 1);
  if (local.toLowerCase() === 'sami') return null;

  const ext = extensionOf(filename);
  const claimsXml = (ext !== null && XML_EXTENSIONS.has(ext)) || hasXmlDeclaration(text);
  if (local === 'tt') {
    const declaration = tag.rawAttributes.find(([name]) =>
      prefix === '' ? name === 'xmlns' : name === `xmlns:${prefix}`,
    );
    const namespace = declaration === undefined ? null : declaration[1];
    if (ttmlNamespaceBase(namespace) !== null) return { kind: 'ttml', namespace };
    return claimsXml ? { kind: 'ttml', namespace } : null;
  }
  if (!claimsXml) return null;
  return { kind: 'other-xml', root: tag.name, label: OTHER_XML[local.toLowerCase()] ?? null };
}

/*───────────────────────────────────────────────────────────────────────────────────────
  Audio
───────────────────────────────────────────────────────────────────────────────────────*/

export type AudioKind = 'mp3' | 'wav' | 'flac' | 'ogg' | 'aac' | 'm4a' | 'opus' | 'wma' | 'aiff';

const AUDIO_EXTENSIONS: Readonly<Record<string, AudioKind>> = {
  mp3: 'mp3',
  wav: 'wav',
  wave: 'wav',
  flac: 'flac',
  ogg: 'ogg',
  oga: 'ogg',
  opus: 'opus',
  aac: 'aac',
  m4a: 'm4a',
  m4b: 'm4a',
  wma: 'wma',
  aif: 'aiff',
  aiff: 'aiff',
};

/**
 * AUDIO DETECTION, and the reason it is here rather than deferred to the server: B6 requires
 * the cost warning to be shown BEFORE anything is uploaded. Detecting after the upload means
 * the user has already spent the bandwidth and, on a slow connection, already waited.
 *
 * Magic bytes first, extension second — but the extension is trusted for audio in a way it is
 * not for containers, because the consequence of a false positive is a dialogue the user can
 * cancel rather than a failed parse.
 */
export function sniffAudio(bytes: Uint8Array, filename?: string | null): AudioKind | null {
  if (bytes.length >= 12) {
    if (ascii(bytes, 0, 4) === 'RIFF' && ascii(bytes, 8, 4) === 'WAVE') return 'wav';
    if (ascii(bytes, 0, 4) === 'FORM' && ascii(bytes, 8, 4).startsWith('AIF')) return 'aiff';
    if (ascii(bytes, 0, 4) === 'fLaC') return 'flac';
    if (ascii(bytes, 0, 4) === 'OggS') {
      const head = ascii(bytes, 28, Math.min(40, bytes.length - 28));
      return head.includes('Opus') ? 'opus' : 'ogg';
    }
    if (ascii(bytes, 0, 3) === 'ID3') return 'mp3';
    // An MP4 container whose brand is audio-only: `M4A `, `M4B `.
    if (ascii(bytes, 4, 4) === 'ftyp') {
      const brand = ascii(bytes, 8, 4);
      if (brand.startsWith('M4A') || brand.startsWith('M4B')) return 'm4a';
    }
  }
  // A bare MPEG audio frame: sync bits 0xFF 0xEx/0xFx.
  if (bytes.length >= 2 && bytes[0] === 0xff && ((bytes[1] as number) & 0xe0) === 0xe0) {
    const ext = extensionOf(filename);
    if (ext === null || AUDIO_EXTENSIONS[ext] !== undefined) return ext === 'aac' ? 'aac' : 'mp3';
  }
  const ext = extensionOf(filename);
  if (ext !== null) {
    const byExtension = AUDIO_EXTENSIONS[ext];
    if (byExtension !== undefined) return byExtension;
  }
  return null;
}

/*───────────────────────────────────────────────────────────────────────────────────────
  The one entry point
───────────────────────────────────────────────────────────────────────────────────────*/

export type InputClass =
  | { readonly kind: 'subtitle-text' }
  /**
   * `demuxable` is what the host branches on, so it never holds its own list of readable
   * containers — see `DEMUXABLE`. False means `containerRefusal(container)` has the reason.
   */
  | { readonly kind: 'container'; readonly container: ContainerKind; readonly demuxable: boolean }
  | { readonly kind: 'audio'; readonly audio: AudioKind }
  | { readonly kind: 'bitmap-subtitle'; readonly bitmap: BitmapFile; readonly label: string }
  | { readonly kind: 'archive' }
  | { readonly kind: 'unreadable-binary'; readonly detail: string };

/**
 * What to do with a dropped file, decided ONCE.
 *
 * ORDER MATTERS and it is not the obvious one:
 *
 *   1. **Archives first**, because a ZIP of SRTs must expand rather than parse.
 *   2. **Bitmap subtitles before containers**, because a VobSub `.sub` IS an MPEG program stream
 *      and would otherwise be routed to ffmpeg as media.
 *   3. **Audio before containers**, because an Ogg Opus file and an Ogg video file share the
 *      `OggS` signature, and an `M4A` brand shares MP4's `ftyp`. Getting this backwards sends an
 *      audio file to the container host, which finds no subtitle track and reports the wrong
 *      thing — the user needed B6's cost warning, and got "no subtitles inside this video".
 *   4. **Text last**, because it is the only answer that cannot be established by a signature
 *      and is therefore the residue.
 */
export function classifyInput(bytes: Uint8Array, filename?: string | null): InputClass {
  if (isZipArchive(bytes)) return { kind: 'archive' };

  const bitmap = sniffBitmapFile(bytes, filename);
  if (bitmap !== null) {
    return { kind: 'bitmap-subtitle', bitmap, label: bitmapLabel(bitmap) };
  }

  const audio = sniffAudio(bytes, filename);
  if (audio !== null) return { kind: 'audio', audio };

  const container = sniffContainer(bytes, filename);
  if (container !== null) {
    return { kind: 'container', container, demuxable: DEMUXABLE.has(container) };
  }

  if (looksBinary(bytes)) {
    return {
      kind: 'unreadable-binary',
      detail: 'this file contains binary data rather than text, and no signature we recognize',
    };
  }
  return { kind: 'subtitle-text' };
}

/** `PK\x03\x04`, plus the empty-archive and spanned-archive variants. */
export function isZipArchive(bytes: Uint8Array): boolean {
  if (bytes.length < 4) return false;
  if (bytes[0] !== 0x50 || bytes[1] !== 0x4b) return false;
  const c = bytes[2];
  const d = bytes[3];
  return (c === 3 && d === 4) || (c === 5 && d === 6) || (c === 7 && d === 8);
}

/*───────────────────────────────────────────────────────────────────────────────────────
  Helpers
───────────────────────────────────────────────────────────────────────────────────────*/

function ascii(bytes: Uint8Array, at: number, length: number): string {
  let out = '';
  for (let i = at; i < at + length && i < bytes.length; i++) {
    out += String.fromCharCode(bytes[i] as number);
  }
  return out;
}

export function extensionOf(filename?: string | null): string | null {
  if (filename === undefined || filename === null) return null;
  const dot = filename.lastIndexOf('.');
  if (dot < 0 || dot === filename.length - 1) return null;
  return filename.slice(dot + 1).toLowerCase();
}

/**
 * A NUL byte or a high proportion of control characters in the first kilobyte.
 *
 * A NUL alone is not enough: UTF-16 text is full of them, and the adapter decodes UTF-16
 * before a parser ever sees it — but `classifyInput` runs on the RAW bytes, before decoding,
 * so a UTF-16 SRT must not be called binary. The BOM check is what separates them.
 */
export function looksBinary(bytes: Uint8Array): boolean {
  if (bytes.length === 0) return false;
  // UTF-16/32 BOMs: text, whatever the NUL density says.
  if (bytes.length >= 2) {
    const b0 = bytes[0] as number;
    const b1 = bytes[1] as number;
    if ((b0 === 0xff && b1 === 0xfe) || (b0 === 0xfe && b1 === 0xff)) return false;
  }
  const limit = Math.min(1024, bytes.length);
  let control = 0;
  for (let i = 0; i < limit; i++) {
    const b = bytes[i] as number;
    if (b === 0) return true;
    // Everything below 0x20 except tab, LF, CR and form feed.
    if (b < 0x20 && b !== 0x09 && b !== 0x0a && b !== 0x0d && b !== 0x0c) control++;
  }
  return control / limit > 0.06;
}
